import React from 'react';
import { Modal, Form, Input, message, Row, Col } from 'antd';
import { useCreateAirportMutation, useUpdateAirportMutation, Airport } from '../../store/api/referencesApi';

export interface CreateAirportFormData {
  name: string;
  iataCode?: string;
  icaoCode?: string;
  city?: string;
  region?: string;
  country?: string;
}

interface CreateAirportModalProps {
  visible: boolean;
  onCancel: () => void;
  onSuccess: (airport: Airport) => void;
  airport?: Airport | null; // Для режима редактирования
  title?: string;
}

const CreateAirportModal: React.FC<CreateAirportModalProps> = ({
  visible,
  onCancel,
  onSuccess,
  airport = null,
  title = 'Добавить аэропорт'
}) => {
  const [form] = Form.useForm();
  const [createAirport, { isLoading: isCreating }] = useCreateAirportMutation();
  const [updateAirport, { isLoading: isUpdating }] = useUpdateAirportMutation();

  const isLoading = isCreating || isUpdating;
  const isEditMode = !!airport;

  const handleSubmit = async (values: CreateAirportFormData) => {
    try {
      console.log('✈️ Creating/Updating airport:', values);
      
      if (isEditMode && airport) {
        const updatedAirport = await updateAirport({
          id: airport.id,
          data: {
            ...values,
            country: values.country || 'Россия'
          }
        }).unwrap();
        
        console.log('✅ Airport updated successfully:', updatedAirport);
        message.success('Аэропорт успешно обновлен');
        onSuccess(updatedAirport);
      } else {
        const newAirport = await createAirport({
          ...values,
          country: values.country || 'Россия'
        }).unwrap();
        
        console.log('✅ Airport created successfully:', newAirport);
        message.success('Аэропорт успешно создан');
        onSuccess(newAirport);
      }
      
      form.resetFields();
      onCancel();
    } catch (error: any) {
      console.error('❌ Error creating/updating airport:', error);
      const errorMessage = error?.data?.error || 'Ошибка при создании/обновлении аэропорта';
      message.error(errorMessage);
    }
  };

  const handleCancel = () => {
    form.resetFields();
    onCancel();
  };

  // Заполняем форму данными для редактирования
  React.useEffect(() => {
    if (visible && airport) {
      form.setFieldsValue({
        name: airport.name,
        iataCode: airport.iataCode,
        icaoCode: airport.icaoCode,
        city: airport.city,
        region: airport.region,
        country: airport.country
      });
    } else if (visible && !airport) {
      form.resetFields();
      form.setFieldsValue({ country: 'Россия' });
    }
  }, [visible, airport, form]);

  return (
    <Modal
      title={isEditMode ? 'Редактировать аэропорт' : title}
      open={visible}
      onOk={() => form.submit()}
      onCancel={handleCancel}
      okText={isEditMode ? 'Обновить' : 'Создать'}
      cancelText="Отмена"
      confirmLoading={isLoading}
      width={600}
    >
      <Form
        form={form}
        layout="vertical"
        onFinish={handleSubmit}
        initialValues={{ country: 'Россия' }}
      >
        <Form.Item
          label="Название аэропорта"
          name="name"
          rules={[
            { required: true, message: 'Введите название аэропорта' },
            { min: 2, message: 'Название должно содержать минимум 2 символа' }
          ]}
        >
          <Input placeholder="Например: Шереметьево" maxLength={255} />
        </Form.Item>

        <Row gutter={16}>
          <Col xs={24} md={12}>
            <Form.Item
              label="IATA код"
              name="iataCode"
              rules={[
                {
                  pattern: /^[A-Z]{3}$/,
                  message: 'IATA код должен содержать 3 заглавные латинские буквы'
                }
              ]}
            >
              <Input 
                placeholder="SVO"
                maxLength={3}
                style={{ textTransform: 'uppercase' }}
              />
            </Form.Item>
          </Col>
          <Col xs={24} md={12}>
            <Form.Item
              label="ICAO код"
              name="icaoCode"
              rules={[
                {
                  pattern: /^[A-Z]{4}$/,
                  message: 'ICAO код должен содержать 4 заглавные латинские буквы'
                }
              ]}
            >
              <Input 
                placeholder="UUEE"
                maxLength={4}
                style={{ textTransform: 'uppercase' }}
              />
            </Form.Item>
          </Col>
        </Row>

        <Row gutter={16}>
          <Col xs={24} md={12}>
            <Form.Item
              label="Город"
              name="city"
            >
              <Input 
                placeholder="Название города"
                maxLength={100}
              />
            </Form.Item>
          </Col>
          <Col xs={24} md={12}>
            <Form.Item
              label="Регион"
              name="region"
            >
              <Input 
                placeholder="Область/край/республика"
                maxLength={100}
              />
            </Form.Item>
          </Col>
        </Row>

        <Form.Item
          label="Страна"
          name="country"
        >
          <Input 
            placeholder="Страна"
            maxLength={100}
          />
        </Form.Item>
      </Form>
    </Modal>
  );
};

export default CreateAirportModal; 